<script setup>
import { computed, ref, watch } from 'vue';
import getWootAxios from 'dashboard/helper/getWootAxios';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { defaultRedirectPageForAccount } from 'dashboard/helper/routeHelpers';

import {
  DropdownContainer,
  DropdownBody,
  DropdownSection,
  DropdownItem,
} from 'next/dropdown-menu/base';
import Logo from 'next/icon/Logo.vue';
import AccountMemoryPackPill from 'dashboard/components-next/ailab/AccountMemoryPackPill.vue';
import SidebarNotificationBell from 'dashboard/components-next/sidebar/SidebarNotificationBell.vue';

const axios = getWootAxios();
const { t } = useI18n();
const { accountId, currentAccount, accountScopedRoute } = useAccount();
const route = useRoute();
const router = useRouter();
const store = useStore();
const currentUser = useMapGetter('getCurrentUser');
const currentUserAvailability = useMapGetter('getCurrentUserAvailability');
const currentUserAcceptsChat = useMapGetter('getCurrentUserAcceptsChat');
const userAccounts = useMapGetter('getUserAccounts');

const showAccountSwitcher = computed(
  () => userAccounts.value.length > 1 && currentAccount.value.name
);

const isAiLabPage = computed(() => {
  const path = route?.path?.toString() || '';
  return path.includes('/ailab');
});

const sortedCurrentUserAccounts = computed(() => {
  return [...(currentUser.value.accounts || [])].sort((a, b) =>
    a.name.localeCompare(b.name)
  );
});

const ACCOUNT_SWITCHER_COUNT_CACHE_TTL_MS = 15000;
const ACCOUNT_SWITCHER_COUNT_MODE = {
  UNREAD: 'unread',
  UNRESOLVED: 'unresolved',
};
const accountSwitcherCounts = ref({});
const isAccountSwitcherCountsLoading = ref(false);
const accountSwitcherCountsFetchedAt = ref(0);

const normalizeCount = value => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : 0;
};

const accountListKey = computed(() =>
  sortedCurrentUserAccounts.value.map(account => account.id).join(',')
);

const accountSwitcherCountMode = computed(() => {
  const configuredMode =
    currentAccount.value?.settings?.account_switcher_count_mode;
  return configuredMode === ACCOUNT_SWITCHER_COUNT_MODE.UNRESOLVED
    ? ACCOUNT_SWITCHER_COUNT_MODE.UNRESOLVED
    : ACCOUNT_SWITCHER_COUNT_MODE.UNREAD;
});

const accountSwitcherCountField = computed(() =>
  accountSwitcherCountMode.value === ACCOUNT_SWITCHER_COUNT_MODE.UNRESOLVED
    ? 'unresolved_count'
    : 'unread_count'
);

const hasCountProperty = (account, field) =>
  Object.prototype.hasOwnProperty.call(account || {}, field);

const syncCountMapWithAccounts = (accountsPayload = null) => {
  const countField = accountSwitcherCountField.value;
  const payloadById = {};
  if (Array.isArray(accountsPayload)) {
    accountsPayload.forEach(account => {
      if (!account?.id) return;
      payloadById[account.id] = account;
    });
  }

  const next = {};
  sortedCurrentUserAccounts.value.forEach(account => {
    const payloadAccount = payloadById[account.id];
    if (payloadAccount && hasCountProperty(payloadAccount, countField)) {
      next[account.id] = normalizeCount(payloadAccount[countField]);
      return;
    }

    if (hasCountProperty(account, countField)) {
      next[account.id] = normalizeCount(account[countField]);
      return;
    }

    next[account.id] = normalizeCount(accountSwitcherCounts.value[account.id]);
  });
  accountSwitcherCounts.value = next;
};

const shouldRefreshAccountSwitcherCounts = forceRefresh => {
  if (forceRefresh) return true;
  if (!accountSwitcherCountsFetchedAt.value) return true;
  return (
    Date.now() - accountSwitcherCountsFetchedAt.value >
    ACCOUNT_SWITCHER_COUNT_CACHE_TTL_MS
  );
};

const fetchAccountSwitcherCounts = async ({ forceRefresh = false } = {}) => {
  if (!showAccountSwitcher.value) return;
  if (isAccountSwitcherCountsLoading.value) return;
  if (!shouldRefreshAccountSwitcherCounts(forceRefresh)) return;

  const accounts = sortedCurrentUserAccounts.value;
  if (!accounts.length) return;

  // Keep the badge populated from the latest user payload
  syncCountMapWithAccounts();

  isAccountSwitcherCountsLoading.value = true;
  try {
    // Single non-account-scoped request avoids cross-account 401s.
    const { data } = await axios.get('/api/v1/profile');
    const fetchedAccounts = Array.isArray(data?.accounts) ? data.accounts : [];
    syncCountMapWithAccounts(fetchedAccounts);
    accountSwitcherCountsFetchedAt.value = fetchedAccounts.length
      ? Date.now()
      : 0;
  } catch (error) {
    accountSwitcherCountsFetchedAt.value = 0;
  } finally {
    isAccountSwitcherCountsLoading.value = false;
  }
};

const countByAccount = accountIdValue =>
  normalizeCount(accountSwitcherCounts.value[accountIdValue]);

const countLabelByAccount = accountIdValue => {
  const count = countByAccount(accountIdValue);
  return count > 99 ? '99+' : String(count);
};

const accountRoleLabel = account =>
  account.custom_role_id
    ? account.custom_role?.name || account.role
    : account.role;

const onAccountSwitcherTriggerClick = (toggle, isOpen) => {
  if (!showAccountSwitcher.value) return;
  if (!isOpen) {
    fetchAccountSwitcherCounts({ forceRefresh: true });
  }
  toggle();
};

watch(
  [accountListKey, accountSwitcherCountMode],
  () => {
    syncCountMapWithAccounts();
    fetchAccountSwitcherCounts({ forceRefresh: true });
  },
  { immediate: true }
);

const onChangeAccount = newId => {
  const targetAccount =
    sortedCurrentUserAccounts.value.find(
      account => Number(account.id) === Number(newId)
    ) || {};
  const targetPath = defaultRedirectPageForAccount(
    newId,
    targetAccount.permissions || []
  );
  const accountUrl = `/app/${targetPath}`;
  window.location.href = accountUrl;
};

const acceptsChatOn = computed(() => currentUserAcceptsChat.value !== false);
const isOnline = computed(() => currentUserAvailability.value === 'online');
const bannerText = computed(() => {
  if (!acceptsChatOn.value) return t('DASHBOARD_TOPBAR.NOT_ACCEPTING_CHATS');
  if (!isOnline.value) return t('DASHBOARD_TOPBAR.ACCEPTING_CHATS_OFFLINE');
  return t('DASHBOARD_TOPBAR.ACCEPTING_CHATS');
});
const startAcceptingChatsLabel = computed(() =>
  t('DASHBOARD_TOPBAR.START_ACCEPTING_CHATS')
);

const toggleAcceptsChat = async desired => {
  await store.dispatch('updateAcceptsChat', {
    accountId: accountId.value,
    acceptsChat: desired,
  });
};

function openNotificationsPage() {
  router.push(accountScopedRoute('notifications_index'));
}
</script>

<template>
  <div
    class="wlab-topbar"
    :class="acceptsChatOn ? 'wlab-topbar--on' : 'wlab-topbar--off'"
    role="status"
  >
    <div class="wlab-topbar__left">
      <!-- Account Switcher -->
      <DropdownContainer>
        <template #trigger="{ toggle, isOpen }">
          <button
            class="wlab-topbar__account-btn"
            :class="{ 'wlab-topbar__account-btn--open': isOpen }"
            @click="() => onAccountSwitcherTriggerClick(toggle, isOpen)"
          >
            <span class="wlab-topbar__account-icon">
              <Logo class="size-5" />
            </span>
            <span class="wlab-topbar__account-name">
              {{ currentAccount.name }}
            </span>
            <span
              v-if="showAccountSwitcher"
              class="i-lucide-chevron-down size-4 wlab-topbar__chevron"
              :class="{ 'rotate-180': isOpen }"
            />
          </button>
        </template>
        <DropdownBody
          v-if="showAccountSwitcher"
          class="mt-2 min-w-64 z-50 wlab-account-switcher-dropdown"
        >
          <DropdownSection :title="t('SIDEBAR_ITEMS.SWITCH_ACCOUNT')">
            <DropdownItem
              v-for="account in sortedCurrentUserAccounts"
              :id="`account-${account.id}`"
              :key="account.id"
              class="cursor-pointer wlab-account-switcher-item"
              @click="onChangeAccount(account.id)"
            >
              <template #label>
                <div class="wlab-account-switcher-row">
                  <div class="wlab-account-switcher-row__main">
                    <span class="wlab-account-switcher-row__logo">
                      <Logo
                        :account="account"
                        class="size-4 rounded-sm object-contain"
                      />
                    </span>
                    <span
                      class="wlab-account-switcher-row__name"
                      :title="account.name"
                    >
                      {{ account.name }}
                    </span>
                    <span class="wlab-account-switcher-row__divider" />
                    <span class="wlab-account-switcher-row__role">
                      {{ accountRoleLabel(account) }}
                    </span>
                  </div>
                  <div class="wlab-account-switcher-row__meta">
                    <span
                      class="wlab-account-switcher-row__unread"
                      :class="{
                        'is-zero': countByAccount(account.id) === 0,
                        'is-loading': isAccountSwitcherCountsLoading,
                      }"
                    >
                      {{ countLabelByAccount(account.id) }}
                    </span>
                    <span
                      v-if="account.id === accountId"
                      class="i-lucide-check size-4 wlab-account-switcher-row__check"
                    />
                  </div>
                </div>
              </template>
            </DropdownItem>
          </DropdownSection>
        </DropdownBody>
      </DropdownContainer>
    </div>

    <div class="wlab-topbar__center">
      <span class="wlab-topbar__dot" aria-hidden="true" />
      <span class="wlab-topbar__text">{{ bannerText }}</span>
    </div>

    <div class="wlab-topbar__right">
      <button
        v-if="!acceptsChatOn"
        class="wlab-topbar__cta"
        type="button"
        @click="() => toggleAcceptsChat(true)"
      >
        {{ startAcceptingChatsLabel }}
      </button>

      <!-- Account Memory Pack (AiLab only) -->
      <AccountMemoryPackPill v-if="isAiLabPage" />

      <!-- Notification Bell -->
      <SidebarNotificationBell
        @open-notification-panel="openNotificationsPage"
      />
    </div>
  </div>
</template>

<style scoped>
.wlab-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 42px;
  padding: 6px 10px;
  margin: 6px 12px 6px 0;
  border-radius: 12px;
  overflow: hidden;
  position: relative;
  /* Smooth color crossfade matching LiveChat's alert transition */
  transition:
    background-color 0.45s cubic-bezier(0.4, 0, 0.2, 1),
    background 0.45s cubic-bezier(0.4, 0, 0.2, 1);
}

@media (max-width: 768px) {
  .wlab-topbar {
    margin: 6px;
  }
}

.wlab-topbar--off {
  background: #ca0005;
  animation: wlabTopbarBounce 2.6s ease-in-out infinite;
}

.wlab-topbar--on {
  background: #0066ff;
}

@keyframes wlabTopbarBounce {
  0%,
  82%,
  100% {
    transform: translateY(0);
  }
  86% {
    transform: translateY(-1px);
  }
  90% {
    transform: translateY(0);
  }
  94% {
    transform: translateY(-1px);
  }
  98% {
    transform: translateY(0);
  }
}

.wlab-topbar__left,
.wlab-topbar__right {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.wlab-topbar__center {
  display: flex;
  align-items: center;
  gap: 10px;
  justify-content: center;
  flex: 1;
  min-width: 0;
  padding: 0 10px;
}

.wlab-topbar__dot {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: rgba(248, 255, 237, 0.92);
  box-shadow: 0 0 0 0 rgba(248, 255, 237, 0.55);
  flex-shrink: 0;
}
.wlab-topbar--off .wlab-topbar__dot {
  animation: wlabDotPulse 1.2s ease-in-out infinite;
}

@keyframes wlabDotPulse {
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(248, 255, 237, 0.45);
  }
  70% {
    transform: scale(1.06);
    box-shadow: 0 0 0 10px rgba(248, 255, 237, 0);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(248, 255, 237, 0);
  }
}

.wlab-topbar__text {
  color: rgba(255, 255, 255, 0.95);
  font-size: 12px;
  font-weight: 600;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.wlab-topbar__cta {
  border: none;
  background: rgba(248, 255, 237, 0.95);
  color: #0f1d18;
  border-radius: 999px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  flex-shrink: 0;
}

.wlab-topbar__account-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 3px 6px;
  border-radius: 8px;
  border: none;
  background: rgba(0, 0, 0, 0.18);
  cursor: pointer;
  transition: all 0.2s ease;
}

.wlab-topbar__account-btn:hover {
  background: rgba(0, 0, 0, 0.26);
}

.wlab-topbar__account-btn--open {
  box-shadow: 0 0 0 3px rgba(248, 255, 237, 0.14);
}

.wlab-topbar__account-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
}

.wlab-topbar__account-name {
  font-size: 14px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
}

.wlab-topbar__chevron {
  color: rgba(255, 255, 255, 0.8);
  transition: transform 0.2s ease;
}

.wlab-account-switcher-dropdown {
  --as-bg: #ffffff;
  --as-border: rgba(15, 23, 42, 0.12);
  --as-shadow: 0 16px 46px -12px rgba(15, 23, 42, 0.26),
    0 6px 18px -8px rgba(15, 23, 42, 0.12);
  --as-hover: rgba(15, 23, 42, 0.055);
  --as-title: #64748b;
  --as-text: #0f172a;
  --as-muted: #6b7280;
  --as-divider: rgba(15, 23, 42, 0.12);
  --as-badge-bg: rgba(220, 38, 38, 0.16);
  --as-badge-text: #b91c1c;
  --as-badge-border: rgba(220, 38, 38, 0.28);
  --as-badge-zero-bg: rgba(15, 23, 42, 0.04);
  --as-badge-zero-border: rgba(15, 23, 42, 0.08);
}

:global(body.dark:not(.theme-mix)) .wlab-account-switcher-dropdown,
:global(html.dark body:not(.theme-mix)) .wlab-account-switcher-dropdown {
  --as-bg: #141821;
  --as-border: rgba(255, 255, 255, 0.1);
  --as-shadow: 0 22px 56px -16px rgba(0, 0, 0, 0.58),
    0 8px 20px -10px rgba(0, 0, 0, 0.42);
  --as-hover: rgba(255, 255, 255, 0.075);
  --as-title: #9aa3b2;
  --as-text: #f8fafc;
  --as-muted: #a6b0c2;
  --as-divider: rgba(255, 255, 255, 0.12);
  --as-badge-bg: rgba(248, 113, 113, 0.2);
  --as-badge-text: #fecaca;
  --as-badge-border: rgba(248, 113, 113, 0.35);
  --as-badge-zero-bg: rgba(255, 255, 255, 0.05);
  --as-badge-zero-border: rgba(255, 255, 255, 0.1);
}

.wlab-account-switcher-dropdown :deep(.n-dropdown-body) {
  background: var(--as-bg) !important;
  border: 1px solid var(--as-border) !important;
  border-radius: 14px !important;
  box-shadow: var(--as-shadow) !important;
  backdrop-filter: none !important;
  padding: 8px !important;
  min-width: 340px !important;
  gap: 6px !important;
}

.wlab-account-switcher-dropdown :deep(.n-dropdown-section) {
  margin: 0 !important;
}

.wlab-account-switcher-dropdown
  :deep(.n-dropdown-section > div.text-n-slate-10) {
  color: var(--as-title) !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  margin: 0 0 8px !important;
  padding: 0 10px !important;
  letter-spacing: 0.02em !important;
}

.wlab-account-switcher-dropdown :deep(.n-dropdown-section > ul) {
  gap: 4px !important;
  padding: 0 2px 2px !important;
}

.wlab-account-switcher-dropdown :deep(.n-dropdown-item > *) {
  border-radius: 10px !important;
  padding: 10px !important;
  color: var(--as-text) !important;
}

.wlab-account-switcher-dropdown :deep(.n-dropdown-item > *:hover) {
  background: var(--as-hover) !important;
}

.wlab-account-switcher-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  min-width: 0;
}

.wlab-account-switcher-row__main {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.wlab-account-switcher-row__logo {
  width: 18px;
  height: 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.wlab-account-switcher-row__name {
  color: var(--as-text);
  font-size: 14px;
  font-weight: 600;
  line-height: 1.2;
  max-width: 152px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.wlab-account-switcher-row__divider {
  width: 1px;
  height: 12px;
  flex-shrink: 0;
  background: var(--as-divider);
}

.wlab-account-switcher-row__role {
  color: var(--as-muted);
  font-size: 12px;
  font-weight: 500;
  line-height: 1.2;
  text-transform: capitalize;
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.wlab-account-switcher-row__meta {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.wlab-account-switcher-row__unread {
  min-width: 24px;
  height: 20px;
  border-radius: 999px;
  border: 1px solid var(--as-badge-border);
  background: var(--as-badge-bg);
  color: var(--as-badge-text);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 6px;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
}

.wlab-account-switcher-row__unread.is-zero {
  border-color: var(--as-badge-zero-border);
  background: var(--as-badge-zero-bg);
  color: var(--as-muted);
}

.wlab-account-switcher-row__unread.is-loading {
  opacity: 0.8;
}

.wlab-account-switcher-row__check {
  color: #10b981;
}
</style>
